import { batch } from 'react-redux';

import { getConferenceState } from '../base/conference';
import { MHConferenceEvents } from '../base/lib-meet-hour';
import { MEDIA_TYPE } from '../base/media';
import {
    PARTICIPANT_UPDATED,
    getParticipantDisplayName,
    isLocalParticipantModerator,
    raiseHand
} from '../base/participants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import {
    NOTIFICATION_TIMEOUT,
    hideNotification,
    showNotification
} from '../notifications';

import {
    DISABLE_MODERATION,
    ENABLE_MODERATION,
    LOCAL_PARTICIPANT_MODERATION_NOTIFICATION,
    REQUEST_DISABLE_MODERATION,
    REQUEST_ENABLE_MODERATION
} from './actionTypes';
import {
    disableModeration,
    dismissPendingAudioParticipant,
    dismissPendingParticipant,
    enableModeration,
    localParticipantApproved,
    participantApproved,
    participantPendingAudio
} from './actions';
import {
    isEnabledFromState,
    isParticipantApproved,
    isParticipantPending
} from './functions';

const VIDEO_MODERATION_NOTIFICATION_ID = 'video-moderation';
const AUDIO_MODERATION_NOTIFICATION_ID = 'audio-moderation';
const CS_MODERATION_NOTIFICATION_ID = 'video-moderation';

let _avModerationHandlers = null;

MiddlewareRegistry.register(({ dispatch, getState }) => next => action => {
    const { actor, mediaType, type } = action;

    switch (type) {
    case DISABLE_MODERATION:
    case ENABLE_MODERATION: {
        // Audio & video moderation are both enabled at the same time.
        // Avoid displaying 2 different notifications.
        if (mediaType === MEDIA_TYPE.VIDEO) {
            const titleKey = type === ENABLE_MODERATION
                ? 'notify.moderationStartedTitle'
                : 'notify.moderationStoppedTitle';

            dispatch(showNotification({
                descriptionKey: actor ? 'notify.moderationToggleDescription' : undefined,
                descriptionArguments: actor ? {
                    participantDisplayName: getParticipantDisplayName(getState, actor.getId())
                } : undefined,
                titleKey
            }, NOTIFICATION_TIMEOUT));
        }

        break;
    }
    case LOCAL_PARTICIPANT_MODERATION_NOTIFICATION: {
        let descriptionKey;
        let titleKey;
        let uid;

        switch (action.mediaType) {
        case MEDIA_TYPE.AUDIO: {
            titleKey = 'notify.moderationInEffectTitle';
            descriptionKey = 'notify.moderationInEffectDescription';
            uid = AUDIO_MODERATION_NOTIFICATION_ID;
            break;
        }
        case MEDIA_TYPE.VIDEO: {
            titleKey = 'notify.moderationInEffectVideoTitle';
            descriptionKey = 'notify.moderationInEffectVideoDescription';
            uid = VIDEO_MODERATION_NOTIFICATION_ID;
            break;
        }
        case MEDIA_TYPE.PRESENTER: {
            titleKey = 'notify.moderationInEffectCSTitle';
            descriptionKey = 'notify.moderationInEffectCSDescription';
            uid = CS_MODERATION_NOTIFICATION_ID;
            break;
        }
        }

        dispatch(showNotification({
            customActionNameKey: 'notify.raiseHandAction',
            customActionHandler: () => batch(() => {
                dispatch(raiseHand(true));
                dispatch(hideNotification(uid));
            }),
            descriptionKey,
            sticky: true,
            titleKey,
            uid
        }));

        break;
    }
    case REQUEST_DISABLE_MODERATION: {
        const { conference } = getConferenceState(getState());

        conference.disableAVModeration(MEDIA_TYPE.AUDIO);
        conference.disableAVModeration(MEDIA_TYPE.VIDEO);
        break;
    }
    case REQUEST_ENABLE_MODERATION: {
        const { conference } = getConferenceState(getState());

        conference.enableAVModeration(MEDIA_TYPE.AUDIO);
        conference.enableAVModeration(MEDIA_TYPE.VIDEO);
        break;
    }
    case PARTICIPANT_UPDATED: {
        const state = getState();
        const audioModerationEnabled = isEnabledFromState(MEDIA_TYPE.AUDIO, state);

        // this is handled only by moderators
        if (audioModerationEnabled && isLocalParticipantModerator(state)) {
            const { participant: { id, raisedHand } } = action;

            if (raisedHand) {
                // if participant raises hand show notification
                !isParticipantApproved(id, MEDIA_TYPE.AUDIO)(state) && dispatch(participantPendingAudio(id));
            } else {
                // if participant lowers hand hide notification
                isParticipantPending(id, MEDIA_TYPE.AUDIO)(state) && dispatch(dismissPendingAudioParticipant(id));
            }
        }

        break;
    }
    }

    return next(action);
});

/**
 * Registers the conference event listeners needed for the A/V moderation
 * feature to operate. The toggle commands are sent by the middleware above;
 * these events are the server confirmation path that updates Redux and drives
 * notifications.
 */
StateListenerRegistry.register(
    state => state['features/base/conference'].conference,
    (conference, { dispatch }, previousConference) => {
        if (previousConference && _avModerationHandlers) {
            previousConference.off(MHConferenceEvents.AV_MODERATION_APPROVED, _avModerationHandlers.localApproved);
            previousConference.off(MHConferenceEvents.AV_MODERATION_CHANGED, _avModerationHandlers.changed);
            previousConference.off(
                MHConferenceEvents.AV_MODERATION_PARTICIPANT_APPROVED,
                _avModerationHandlers.participantApproved
            );
            _avModerationHandlers = null;
        }

        if (conference && conference !== previousConference) {
            const localApproved = ({ mediaType }) => {
                dispatch(localParticipantApproved(mediaType));

                // Audio & video moderation are enabled together by this UI.
                // Only show one "host asked you to unmute" notification.
                if (mediaType === MEDIA_TYPE.VIDEO) {
                    dispatch(showNotification({
                        descriptionKey: 'notify.hostAskedUnmute',
                        sticky: true,
                        titleKey: 'notify.unmute'
                    }));
                }
            };
            const changed = ({ enabled, mediaType, actor }) => {
                dispatch(enabled ? enableModeration(mediaType, actor) : disableModeration(mediaType, actor));
            };
            const onParticipantApproved = ({ participant, mediaType }) => {
                const id = participant?.getId?.() ?? participant?._id;

                if (!id) {
                    return;
                }

                batch(() => {
                    dispatch(participantApproved(id, mediaType));
                    dispatch(dismissPendingParticipant(id, mediaType));
                });
            };

            conference.on(MHConferenceEvents.AV_MODERATION_APPROVED, localApproved);
            conference.on(MHConferenceEvents.AV_MODERATION_CHANGED, changed);
            conference.on(MHConferenceEvents.AV_MODERATION_PARTICIPANT_APPROVED, onParticipantApproved);

            _avModerationHandlers = {
                changed,
                localApproved,
                participantApproved: onParticipantApproved
            };
        }
    });
